import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader, StatCard } from "../components/PageHeader";
import api, { formatINR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CaretLeft, CaretRight, CheckCircle, XCircle, Clock, Bed, Confetti } from "@phosphor-icons/react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const STATUS_ICONS = {
  Present: { icon: CheckCircle, color: "#10B981" },
  Absent: { icon: XCircle, color: "#DC2626" },
  "Half-Day": { icon: Clock, color: "#FBBF24" },
  Leave: { icon: Bed, color: "#6B7280" },
  Holiday: { icon: Confetti, color: "#8B5CF6" },
};

export default function Payroll() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [employeeId, setEmployeeId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [error, setError] = useState("");
  const [showIncentive, setShowIncentive] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/employees")
      .then(({ data }) => setEmployees(data))
      .catch(() => { /* handled by empty state */ });
  }, [isAdmin]);

  const loadSummary = useCallback(() => {
    const params = { year, month };
    if (isAdmin && employeeId) params.employee_id = employeeId;
    setStatus("loading");
    setError("");
    api.get("/payroll/summary", { params })
      .then(({ data }) => { setData(data); setStatus("ok"); })
      .catch((err) => {
        setData(null);
        setStatus("error");
        setError(err?.response?.data?.detail || err?.message || "Could not load payroll");
      });
  }, [year, month, employeeId, isAdmin]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const changeMonth = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const daysGrouped = useMemo(() => {
    if (!data) return [];
    return data.days;
  }, [data]);

  return (
    <div>
      <PageHeader
        eyebrow="Earnings"
        title="Payroll"
        subtitle={isAdmin ? "View any employee's per-day earnings for the selected month." : "Your day-by-day earnings for the selected month."}
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="payroll-body">
        {status === "error" && (
          <div className="bg-[#FEF2F2] border border-[#FCA5A5] text-[#7F1D1D] p-4 text-sm flex flex-wrap items-center justify-between gap-3" data-testid="payroll-error">
            <div>
              <div className="font-semibold">Couldn't load your payroll</div>
              <div className="text-xs mt-1">{error}</div>
              <div className="text-xs mt-1 text-[#7F1D1D]/80">
                If this keeps happening, hard-refresh with <kbd className="border border-[#FCA5A5] px-1">Ctrl</kbd>+<kbd className="border border-[#FCA5A5] px-1">Shift</kbd>+<kbd className="border border-[#FCA5A5] px-1">R</kbd> (or <kbd className="border border-[#FCA5A5] px-1">Cmd</kbd>+<kbd className="border border-[#FCA5A5] px-1">Shift</kbd>+<kbd className="border border-[#FCA5A5] px-1">R</kbd> on Mac) to clear the cached page.
              </div>
            </div>
            <button onClick={loadSummary} data-testid="payroll-retry"
                    className="brand-btn px-4 py-2 text-sm">Retry</button>
          </div>
        )}
        {/* Controls */}
        <div className="flex flex-wrap items-end gap-3 bg-white border border-[#E5E7EB] p-4">
          <div className="flex items-center border border-[#E5E7EB]">
            <button data-testid="payroll-prev-month" onClick={() => changeMonth(-1)} className="p-2 hover:bg-[#F9FAFB]"><CaretLeft size={14} /></button>
            <div className="px-4 py-2 text-sm font-semibold min-w-[160px] text-center" data-testid="payroll-month-label">{MONTHS[month - 1]} {year}</div>
            <button data-testid="payroll-next-month" onClick={() => changeMonth(1)} className="p-2 hover:bg-[#F9FAFB]"><CaretRight size={14} /></button>
          </div>
          {isAdmin && (
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} data-testid="payroll-emp-select" className="border border-[#E5E7EB] px-3 py-2 text-sm">
              <option value="">Myself</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.employee_id} — {e.name}</option>)}
            </select>
          )}
        </div>

        {/* Stat grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Basic Salary" value={formatINR(data?.employee?.basic_salary || 0)} testId="stat-basic-salary" />
          <StatCard label="Per Day Gross" value={formatINR(data?.per_day_gross || 0)}
                    hint={`Working days: ${data?.working_days || 0}`} testId="stat-perday" />
          <StatCard label="Days Present" value={data?.days_present ?? 0}
                    hint={`A:${data?.days_absent||0} H:${data?.days_half||0} L:${data?.days_leave||0}`} testId="stat-days-present" />
          <StatCard label="Gross Earned" value={formatINR(data?.gross_earned || 0)}
                    hint={data?.achievement_pct !== null && data?.achievement_pct !== undefined && data.achievement_pct < 50
                      ? `Perf-adjusted on Gross Full ${formatINR(data?.gross_full || 0)}`
                      : "Attendance-based"} testId="stat-gross-earned" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard label="Total Payable" value={formatINR(data?.total_payable || 0)}
                    hint="Gross earned + performance incentive (expenses excluded)" testId="stat-total-payable" />
          <StatCard label="Employee" value={data?.employee?.name || "—"}
                    hint={data?.employee?.employee_id ? `${data.employee.employee_id} · ${data.employee.department}` : ""} testId="stat-emp-name" />
        </div>

        {(data?.achievement_pct !== null && data?.achievement_pct !== undefined) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="sales-payroll-block">
            <StatCard label="Sales Achievement" value={`${data.achievement_pct}%`}
                      hint={data.achievement_pct < 50 ? "Below 50% — salary = Gross × pct%" : data.achievement_pct >= 100 ? "Target hit ✓" : "≥50% — full attendance salary"} testId="stat-achievement" />
            <StatCard label="Low-Performance Deduction" value={formatINR(data.sales_deduction || 0)}
                      hint={data.sales_deduction > 0 ? "Gross Full − Perf-adjusted salary" : "No deduction applied"} testId="stat-sales-deduction" />
            <div className="bg-white border border-[#E5E7EB] p-5 flex flex-col justify-between" data-testid="stat-incentive-card">
              <div>
                <div className="label-caps text-[#6B7280]">Incentive</div>
                <div className="text-2xl font-display font-bold mt-1 text-[#111827]">{formatINR(data.incentive || 0)}</div>
                <div className="text-xs text-[#6B7280] mt-1">Admin-set bonus for over-target performance.</div>
              </div>
              {isAdmin && (
                <button
                  onClick={() => setShowIncentive(true)}
                  data-testid="set-incentive-btn"
                  className="mt-3 self-start border border-[#106109] text-[#106109] hover:bg-[#F0F7EF] px-3 py-1.5 text-xs font-semibold"
                >
                  {(data.incentive || 0) > 0 ? "Edit incentive" : "Set incentive"}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Day table */}
        <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr className="text-left">
                {["Date", "Day", "Status", "Earning (₹)"].map((h) => (
                  <th key={h} className="px-4 py-3 label-caps">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {daysGrouped.map((d) => {
                const S = d.status ? STATUS_ICONS[d.status] : null;
                const dayName = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d.weekday];
                const holidayRow = d.is_holiday && !d.pre_join;
                return (
                  <tr key={d.date} className={`border-b border-[#F3F4F6] ${d.pre_join ? "bg-[#F9FAFB]" : holidayRow ? "bg-[#F5F3FF]" : ""}`} data-testid={`payroll-row-${d.date}`}>
                    <td className={`px-4 py-3 font-mono ${d.pre_join ? "text-[#9CA3AF]" : ""}`}>{d.date}</td>
                    <td className="px-4 py-3">
                      {d.is_weekend ? <span className="text-[#9CA3AF]">{dayName} · off</span> : <span className={d.pre_join ? "text-[#9CA3AF]" : ""}>{dayName}</span>}
                    </td>
                    <td className="px-4 py-3">
                      {d.pre_join ? (
                        <span className="text-xs text-[#9CA3AF] italic">Pre-joining</span>
                      ) : holidayRow ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: "#8B5CF6" }}>
                          <Confetti size={14} weight="fill" /> Holiday
                          {d.holiday_name && <span className="text-[#6B21A8]/80 font-normal">· {d.holiday_name}</span>}
                        </span>
                      ) : d.status ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: S.color }}>
                          <S.icon size={14} weight="fill" /> {d.status}
                        </span>
                      ) : d.is_weekend ? <span className="text-xs text-[#9CA3AF]">Weekend</span>
                        : <span className="text-xs text-[#9CA3AF]">Not marked</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">
                      {d.earning > 0 ? formatINR(d.earning) : <span className="text-[#9CA3AF]">—</span>}
                    </td>
                  </tr>
                );
              })}
              {daysGrouped.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-[#9CA3AF]">
                  {status === "loading" ? "Loading…" : status === "error" ? "No data — try Retry above." : "No days to show."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {showIncentive && (
        <SetIncentiveModal
          employee={data?.employee}
          year={year}
          month={month}
          current={data?.incentive || 0}
          onClose={() => setShowIncentive(false)}
          onSaved={() => { setShowIncentive(false); loadSummary(); }}
        />
      )}
    </div>
  );
}

function SetIncentiveModal({ employee, year, month, current, onClose, onSaved }) {
  const [amount, setAmount] = useState(current || 0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const amt = parseFloat(amount);
      if (!(amt >= 0)) { setSaving(false); return; }
      await api.post("/sales/incentive", {
        employee_id: employee.id,
        year, month,
        amount: amt,
        note,
      });
      onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not save incentive");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="incentive-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-md border border-[#E5E7EB]">
        <div className="border-b border-[#E5E7EB] px-6 py-4">
          <div className="label-caps">Reward</div>
          <h3 className="font-display font-bold text-xl">Set incentive</h3>
          <div className="text-xs text-[#6B7280] mt-1">
            {employee?.name} · {MONTHS[month - 1]} {year}. Adds on top of salary in Total Payable.
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label-caps block mb-1.5">Amount (₹)</label>
            <input type="number" min="0" step="0.01" required autoFocus
                   value={amount} onChange={(e) => setAmount(e.target.value)}
                   data-testid="incentive-amount"
                   className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:border-[#106109]" />
            <div className="text-xs text-[#6B7280] mt-1">Enter 0 to clear an existing incentive.</div>
          </div>
          <div>
            <label className="label-caps block mb-1.5">Note (optional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)}
                   placeholder="e.g., Q3 target 130% — leadership bonus"
                   data-testid="incentive-note"
                   className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:border-[#106109]" />
          </div>
        </div>
        <div className="border-t border-[#E5E7EB] px-6 py-4 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 text-sm border border-[#E5E7EB]">Cancel</button>
          <button type="submit" disabled={saving} data-testid="incentive-save"
                  className="brand-btn flex-1 py-2.5 text-sm font-semibold disabled:opacity-60">
            {saving ? "Saving…" : "Save incentive"}
          </button>
        </div>
      </form>
    </div>
  );
}
